import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
  getSeniorManagerWfhRequest,
  getApplicationStatuses,
  getUserData,
  updateApplicationStatus,
} from '../../services/UserService';
import KeycloakService from '../../services/KeycloakService';
import { SHOW_NOTIFICATION, WFHAttribute } from '../../constants';
import PaginationComponent from '../../components/Pagination/Pagination';
import GenericLoader from '../../components/Loader/GenericLoader';
import WfhStatusCardComponent from '../../components/WfhStatusCard/WfhStatusCardComponent';
import { NO_WFH_REQUESTS } from '../Manage/ManageConstants';

const defaultFilters = () => ({
  status: '',
  employeeId: '',
  startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
});

/**
 * Senior Manager inbox for WFH requests where this user is the Reporting
 * Manager / sole approver because the employee is the only active manager
 * on their project (no peer project manager to approve). Kept disjoint
 * from the regular Manage > WFH Requests tab via reportingManagerFallback.
 */
const SeniorManagerWfhRequestsComponent = () => {
  const dispatch = useDispatch();
  const currentUserId = KeycloakService.getUserDetail();

  const [requests, setRequests] = useState([]);
  const [loader, setLoader] = useState(true);
  const [filters] = useState(defaultFilters());
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [refresh, setRefresh] = useState(false);

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const enrichRequest = async (request) => {
    const empData = await getUserData(request.applicationDTO.employeeId);
    request.empName = `${empData?.data?.firstName || ''} ${
      empData?.data?.lastName || ''
    }`.trim();
    const approversList = await getApplicationStatuses(
      request.applicationDTO.id,
    );
    request.approverList = approversList?.data?.data;
  };

  const loadRequests = useCallback(async () => {
    if (!currentUserId) {
      setLoader(false);
      return;
    }
    setLoader(true);
    const response = await getSeniorManagerWfhRequest(
      currentUserId,
      filters,
      pageSize,
      pageNo,
    );
    if (!response.isError) {
      const pageData = response.data?.data || {};
      const content = pageData.content || [];
      await Promise.all(content.map(enrichRequest));
      setRequests(content);
      setTotalPages(pageData.totalPages || 0);
    } else {
      notify(response.type, response.message);
    }
    setLoader(false);
  }, [currentUserId, filters, pageSize, pageNo]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, refresh]);

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefresh((prev) => !prev);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  };

  const updateApplication = async (templateApplicationResponse) => {
    const response = await updateApplicationStatus(
      templateApplicationResponse,
    );
    if (!response.isError) {
      setRefresh((prev) => !prev);
    }
    notify(response.type, response.message);
    return response;
  };

  return (
    <Box className="main-content-overlay">
      <div className="main-content-div">
        <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
          WFH Requests
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          WFH applications awaiting your approval as Reporting Manager,
          because the employee is the sole manager on their project.
        </Typography>

        {loader ? (
          <GenericLoader
            rectAttributes={WFHAttribute}
            width="100%"
            height={400}
          />
        ) : requests.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '40vh',
            }}
          >
            <Typography color="text.secondary">{NO_WFH_REQUESTS}</Typography>
          </Box>
        ) : (
          requests.map((request) => (
            <WfhStatusCardComponent
              key={request.id}
              wfh={request}
              wfhStatus={request.status}
              isManagerOrAdmin={true}
              updateApplication={updateApplication}
              refresh={refresh}
              setRefresh={setRefresh}
            />
          ))
        )}

        {requests.length !== 0 && (
          <Box className="page-holder" sx={{ marginBottom: '10px' }}>
            <PaginationComponent
              count={totalPages}
              page={page}
              pageChange={pageChange}
              pageSize={pageSize}
              handlePageSizeChange={handlePageSizeChange}
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

export default SeniorManagerWfhRequestsComponent;
